import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box, Button ,Card, CardBody, CardHeader, Container,
  Flex,
  Heading, HStack,
  IconButton,
  Spinner,
  Text, Textarea, useToast
} from "@chakra-ui/react";
import React, {useEffect, useState}  from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import { getCandidateDetail, getJudgeRevision, getJudgesRevisionList, updateCandidateLevel } from "../Api";
import {errorToast, infoToast, successToast} from "../helper/ToastHelper";
import {useAuthentication} from "../hooks/AuthenticationProvider";
import {useTheme} from "../hooks/ThemeProvider";
import { AnswerPropertyKey, Authority, CandidateStatus, Paths, QuestionType, SkillType } from "../model/Constants";
import { AnswerDto } from "../model/Dto";
import { adminKeys } from "../QueryKeys";
import { displayCandidateStatus, displayTime, extractData, getUniqueOrigin, validResourceExtension } from "../Utils";
import AudioPlayer from "./AudioPlayer";
import JudgeRevisionBox from "./JudgeRevisionBox";
import Markdown from "./Markdown";
import RenderResource from "./RenderResource";

const CandidateDetail = () => {
  const authentication = useAuthentication();
  const toast = useToast();
  const queryClient = useQueryClient();

  const isAdmin =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_ADMIN);
  const isJudge =
    authentication?.currentUserQuery?.data?.data?.authorities?.includes(Authority.ROLE_JUDGE) && !isAdmin;

  const params = useParams<{ username: string, publicId: string, candidateToken: string }>();
  const username = params.username || "";
  const candidateToken = params.candidateToken || "";
  const testPublicId = params.publicId || "";
  const navigate = useNavigate();
  const themeContext = useTheme();

  const [levelValue, setLevelValue] = useState<string>(""); // admin

  const candidateQuery = useQuery(adminKeys.candidateDetail(testPublicId, candidateToken), () =>
    getCandidateDetail(username, testPublicId, candidateToken).then(extractData)
  );

  const singleJudgeRevisionQuery = useQuery(adminKeys.candidateRevision(testPublicId, candidateToken), () =>
    getJudgeRevision(username, testPublicId, candidateToken).then(extractData),
    {enabled: isJudge===true}
  );

  const judgesRevisionListQuery = useQuery(adminKeys.candidateRevisionsList(testPublicId, candidateToken), () =>
    getJudgesRevisionList(username, testPublicId, candidateToken).then(extractData),
    {enabled: isAdmin===true}
  );

  useEffect(() => {
    if (levelValue === "" && candidateQuery?.data?.level && levelValue !== candidateQuery?.data?.level)
      setLevelValue(candidateQuery?.data?.level);

  }, [candidateQuery?.data?.level]);

  const updateCandidateMutation = useMutation(({ username, token, testPublicId, level  }: any) =>
    {
      infoToast(toast, "Actualizando candidato")
      return updateCandidateLevel(username, token,testPublicId, level)
    }, {
      onSuccess: () => {
        if (candidateQuery?.data)
          candidateQuery.data.level = levelValue

        successToast(toast, "Candidato actualizado");
        return queryClient.invalidateQueries(adminKeys.candidates());
      },
      onError: () => {
        errorToast(toast, "Error actualizando candidato");
      },
    });

  function handleSaveLevel() {
      updateCandidateMutation.mutate({
        username: candidateQuery?.data?.username,
        token: candidateToken,
        testPublicId: candidateQuery?.data?.testPublicId,
        level: levelValue
      })
  }

  function displayCorrect(answer:AnswerDto) {
    let correct="";
    if (answer.question.questionType===QuestionType.MULTIPLE_CHOICE) {
        correct=answer.correct?"Correcta":"Incorrecta";
    } else {
        correct=answer.correct?"Respuesta guardada correctamente":"La respuesta podría estar vacía";
    }
    return correct;
  }

  const base64ToBlob = (base64:any, type:any) => {
    // Decode base64 string to binary data
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type });
  };


  if (!candidateQuery.data) {
    return <Spinner />;
  } else {
    candidateQuery.data.answers.sort((a, b) => a.answerOrder - b.answerOrder);
    return (
      <Container maxW="container.lg" marginX="auto">
        <Heading as="h2" size='xl'>Reporte de respuestas</Heading>
        <Heading as="h3"size='md'>{candidateQuery.data.testName} - {candidateQuery.data.testPublicId} - {candidateQuery.data.skillType}</Heading>
        { isAdmin &&
          <Heading as="h3" size='md'>{candidateQuery.data.username} - {candidateQuery.data.name}</Heading>
        }
          <Heading as="h3" size='md'>{candidateQuery.data.userRevisionCode}</Heading>

        <br/>
        <Text>Inicio: {displayTime(candidateQuery.data.startTime)}</Text>
        <Text>Fin: {displayTime(candidateQuery.data.endTime)}</Text>
        <Text fontWeight="bold">{displayCandidateStatus(candidateQuery.data.status)}</Text>
        <br/>

        { isAdmin &&
          <>
            <HStack>
              <Text>Banda</Text>
              <Textarea value={levelValue} minW="10em" resize="none" maxLength={6} width="auto"
                onChange={(event)=>setLevelValue(event.target.value)}
                disabled={!(isAdmin || isJudge) }
                rows={1}
              />
              <Button
                marginX={10}
                colorScheme="teal"
                type="submit"
                onClick={() => handleSaveLevel()}
              >
                Guardar
              </Button>
            </HStack>

            <Heading as="h3" size='md'>Revisiones de jueces</Heading>
            { (judgesRevisionListQuery.isSuccess) ?
                ( (judgesRevisionListQuery.data).map((revision) => (
                    <JudgeRevisionBox revision={revision} displayName={true} readOnly={true}/>
                  ))
                ) : (
                  <Spinner />
                )
            }
          </>
        }

        { isJudge &&
            ( (singleJudgeRevisionQuery.isSuccess) ?
              <>
                {/*<HStack>
                  <Text>Banda</Text>
                  <Text>{levelValue}</Text>
                </HStack>*/}
                <JudgeRevisionBox revision={singleJudgeRevisionQuery.data} readOnly={levelValue!==""}/>
              </>
               :
              <Spinner />
            )
        }

        <br/>
        <Heading as="h3" size='md'>Respuestas</Heading>

        {candidateQuery.data.answers.map((answer) => (
          <Box
            key={answer.question.code}
            marginTop="2"
            marginBottom="2"
            padding="2"
            borderWidth="1px"
            borderRadius="lg"
            borderColor="gray.300"
            borderStyle="solid"
          >
            <Text>#{answer.answerOrder} -  {answer.question.code} - {answer.question.level}</Text>
            <Markdown content={answer.question.instructions}  />
            { validResourceExtension(answer.question.body)
                ? <RenderResource
                    resource={answer.question.body}
                    small={true}
                    subdirectory={"Tests/"+ (getUniqueOrigin(answer.question)===''  ?
                        candidateQuery.data.testPublicId : getUniqueOrigin(answer.question))}
                    maxNumberOfReproductions={0}
                />
                : <Markdown content={answer.question.body} />
            }
            <Markdown content={answer.question.statement}  />

            { answer.question.questionType===QuestionType.MULTIPLE_CHOICE &&
              answer.question.options!==undefined &&
                answer.question.options.map((option) => (
                    <Text fontWeight={option.correct===true? "bold" : "normal"}>
                        {option.option} -  {option.text} {option.correct===true? "*" : ""}
                    </Text>
                ))
            }


            { answer.question.questionType===QuestionType.MULTIPLE_CHOICE &&
              answer.selectedOptions!==undefined &&
                answer.selectedOptions.map((option) => (
                  <Box
                    key={option.option}
                    marginTop="2"
                    marginBottom="2"
                    padding="2"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    borderStyle="solid"
                  >
                    <Text>{option.option}</Text>
                    <Text>{displayCorrect(answer)}</Text>
                  </Box>
                ))
            }
            { answer.question.questionType===QuestionType.COMPOSITION &&
                candidateQuery.data.skillType === SkillType.SPEAKING &&
                answer.answerBlob!==null && answer.answerBlob!==undefined &&
                <>
                  <Card bg={themeContext.theme.X_LIGHT_BG}
                      _dark={{ bg: "gray.800" }}>
                    <CardHeader>
                      <Heading size='sm'>{displayCorrect(answer)}</Heading>
                    </CardHeader>
                    <CardBody>
                      <AudioPlayer
                        sourceUrl={URL.createObjectURL(base64ToBlob(answer.answerBlob, "audio/wav"))}
                        maxNumberOfReproductions={0}
                        seek={true}
                      />
                    </CardBody>
                  </Card>
                </>
            }
            { answer.question.questionType===QuestionType.COMPOSITION &&
                candidateQuery.data.skillType === SkillType.WRITING &&
                <>
                  <Card variant="elevated" bg={themeContext.theme.X_LIGHT_BG} >
                    <CardHeader>
                      <Heading size='sm'>{displayCorrect(answer)}</Heading>
                    </CardHeader>
                    <CardBody>
                      <Text whiteSpace="pre-wrap">{answer.properties[AnswerPropertyKey.WRITING_TEXT_ANSWER]}</Text>
                    </CardBody>
                  </Card>
                </>
            }
          </Box>
        ))}

        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
        <br/>
      </Container>
    );
  }
};

export default CandidateDetail;
